import React, { useEffect, useState } from "react";
import {
  BlockWrapper,
  defineBlock,
  useInspectorControl,
  useEditor,
  useSmartProp,
  smartProps,
} from "@smartfire/js-block-engine";
import {
  PanelBody,
  PanelRow,
  SelectControl,
  TextControl,
  ButtonGroup,
  Button,
  ToggleControl,
} from "@wordpress/components";
import { ColorPalette } from "@wordpress/block-editor";
import apiFetch from "@wordpress/api-fetch";
import { EditableImg } from "@smartfire/components";
import style from "./block.module.scss";
import clsx from "clsx";

const Inspector = () => {
  const [linkType, setLinkType] = useSmartProp("linkType");
  const [link, setLink] = useSmartProp("link");
  const [text, setText] = useSmartProp("text");
  const [blank, setBlank] = useSmartProp("blank");
  const [pages, setPages] = useState([]);
  const [mode, setMode] = useSmartProp("mode");
  const [color, setColor] = useSmartProp("color");
  const [buttonColor, setButtonColor] = useSmartProp("buttonColor");
  const [imagePosition, setImagePosition] = useSmartProp("imagePosition");
  const [align, setAlign] = useSmartProp("align");
  const [buttonWidth, setButtonWidth] = useSmartProp("buttonWidth");

  useEffect(() => {
    apiFetch({ path: "wp/v2/pages" }).then((data) => {
      let pagesList = data.map((el) => {
        return {
          value: el.link,
          label: el.title.rendered,
        };
      });
      setPages(pagesList);
    });
  }, []);

  return (
    <>
      <PanelBody title={"Contenu"} initialOpen={true} icon="text">
        <PanelRow>
          <TextControl label={"Texte"} value={text} onChange={setText} />
        </PanelRow>
      </PanelBody>
      <PanelBody title={"Lien"} initialOpen={false} icon="admin-links">
        <PanelRow>
          <SelectControl
            label={"Type de lien"}
            value={linkType}
            onChange={(value) => {
              setLinkType(value);
              setLink("");
            }}
            options={[
              { value: "internal", label: "Interne" },
              { value: "external", label: "Externe" },
            ]}
          />
        </PanelRow>
        <PanelRow>
          <ToggleControl
            label="Ouvrir dans une nouvelle page"
            checked={blank}
            onChange={setBlank}
          />
        </PanelRow>
        <PanelRow>
          {linkType === "internal" ? (
            <SelectControl
              label="Lien"
              value={link}
              onChange={setLink}
              options={pages}
            />
          ) : (
            <TextControl label="Lien" value={link} onChange={setLink} />
          )}
        </PanelRow>
      </PanelBody>
      <PanelBody title={"Style du bouton"} initialOpen={true}>
        <p>Couleur du texte:</p>
        <PanelRow>
          <ColorPalette
            label="Couleur du texte"
            value={color}
            onChange={setColor}
          />
        </PanelRow>
        <p>Couleur du bouton</p>
        <PanelRow>
          <ColorPalette
            label="Couleur du bouton"
            value={buttonColor}
            onChange={setButtonColor}
          />
        </PanelRow>
        <p>Style du bouton:</p>
        <PanelRow>
          <ButtonGroup>
            <Button
              onClick={() => setMode("filled")}
              isPrimary={mode === "filled"}
            >
              Plein
            </Button>
            <Button
              onClick={() => setMode("outlined")}
              isPrimary={mode === "outlined"}
            >
              Outlined
            </Button>
            <Button
              onClick={() => setMode("ghost")}
              isPrimary={mode === "ghost"}
            >
              Ghost
            </Button>
          </ButtonGroup>
        </PanelRow>
        <p>Alignement du bouton:</p>
        <PanelRow>
          <ButtonGroup>
            <Button
              onClick={() => setAlign("flex-start")}
              isPrimary={align === "flex-start"}
            >
              Gauche
            </Button>
            <Button
              onClick={() => setAlign("center")}
              isPrimary={align === "center"}
            >
              Centré
            </Button>
            <Button
              onClick={() => setAlign("flex-end")}
              isPrimary={align === "flex-end"}
            >
              Droite
            </Button>
          </ButtonGroup>
        </PanelRow>
        <PanelRow>
          <ToggleControl
            label={buttonWidth ? "Largeur fixe" : "Largeur automatique"}
            checked={buttonWidth}
            onChange={setButtonWidth}
          />
        </PanelRow>
        <PanelRow>
          <EditableImg smartProp={"image"} />
        </PanelRow>
        <p>Position de l'image : </p>
        <ButtonGroup>
          <Button
            onClick={() => setImagePosition("start")}
            isPrimary={imagePosition === "start"}
          >
            Avant
          </Button>
          <Button
            onClick={() => setImagePosition("end")}
            isPrimary={imagePosition === "end"}
          >
            Après
          </Button>
        </ButtonGroup>
      </PanelBody>
    </>
  );
};

function Block({
  link,
  image,
  text,
  blank,
  mode,
  color,
  buttonColor,
  align,
  buttonWidth,
  imagePosition,
}) {
  useInspectorControl(Inspector);
  const { isEditing } = useEditor();

  return (
    <BlockWrapper.Div
      className={clsx(
        style.block,
        align === "center" && style.center,
        align === "flex-end" && style.right
      )}
    >
      <a
        href={!isEditing && link}
        target={blank && "_blank"}
        className={clsx(
          style.link,
          style[mode],
          style[imagePosition],
          buttonWidth && style.fitcontent,
          isEditing && style.admin
        )}
        style={{ "--button-color": color, "--button-bg-color": buttonColor }}
      >
        {image && (
          <img src={image.url} alt={image.alt} className={style.image} />
        )}
        {text}
      </a>
    </BlockWrapper.Div>
  );
}

export default defineBlock({
  Block,
  smartProps: {
    linkType: smartProps.string.withDefault(""),
    link: smartProps.string.withDefault(""),
    text: smartProps.string.withDefault(""),
    blank: smartProps.bool.withDefault(false),
    mode: smartProps.string.withDefault("filled"),
    color: smartProps.string.withDefault(""),
    buttonColor: smartProps.string.withDefault(""),
    align: smartProps.string.withDefault("flex-start"),
    buttonWidth: smartProps.bool.withDefault(false),
    image: smartProps.image,
    imagePosition: smartProps.string.withDefault("start"),
  },
  setup: {
    title: "Bouton",
  },
});
